/**
 * iter82 - Admin analytics dashboard, Wave 1.
 *
 * Aggregation-only surfaces. No per-user drill-down UI - every table
 * shows counts, funnel percentages, or dataset freshness, never
 * "here's what user X did".
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp, Filter, Home, PartyPopper, Clock, ArrowDownRight, Calendar } from "lucide-react";
import UserDetailDrawer from "./UserDetailDrawer";

export default function AdminAnalytics() {
  const [signups, setSignups] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [moments, setMoments] = useState(null);
  const [playbooks, setPlaybooks] = useState(null);
  const [festivals, setFestivals] = useState(null);
  // iter83 Wave 2
  const [engagement, setEngagement] = useState(null);
  const [concFunnel, setConcFunnel] = useState(null);
  const [pages, setPages] = useState(null);
  const [zeroEng, setZeroEng] = useState(null);
  // iter96 (PP + QQ) — signup source breakdown + recent signups feed.
  const [bySource, setBySource] = useState(null);
  const [recent,   setRecent]   = useState(null);
  // iter97 (RR) — user detail drawer opened from Who's Arriving rows
  const [openUser, setOpenUser] = useState(null);
  // iter83 - test-account filter toggle. Default ON (exclude tests).
  const [includeTest, setIncludeTest] = useState(false);

  useEffect(() => {
    const q = includeTest ? "?include_test=1" : "";
    const q90 = includeTest ? "?days=90&include_test=1" : "?days=90";
    api.get(`/admin/analytics/signups${q90}`).then(r => setSignups(r.data)).catch(() => setSignups({ error: true }));
    api.get(`/admin/analytics/funnel${q}`).then(r => setFunnel(r.data)).catch(() => setFunnel({ error: true }));
    api.get(`/admin/analytics/moments${q}`).then(r => setMoments(r.data)).catch(() => setMoments({ error: true }));
    api.get(`/admin/analytics/playbook-staleness`).then(r => setPlaybooks(r.data)).catch(() => setPlaybooks({ error: true }));
    api.get(`/admin/analytics/festivals-freshness`).then(r => setFestivals(r.data)).catch(() => setFestivals({ error: true }));
    api.get(`/admin/analytics/product-engagement${q}`).then(r => setEngagement(r.data)).catch(() => setEngagement({ error: true }));
    api.get(`/admin/analytics/concierge-funnel${q}`).then(r => setConcFunnel(r.data)).catch(() => setConcFunnel({ error: true }));
    api.get(`/admin/analytics/pages${q}`).then(r => setPages(r.data)).catch(() => setPages({ error: true }));
    api.get(`/admin/analytics/zero-engagement${q}`).then(r => setZeroEng(r.data)).catch(() => setZeroEng({ error: true }));
    api.get(`/admin/analytics/signups-by-source${q}`).then(r => setBySource(r.data)).catch(() => setBySource({ error: true }));
    api.get(`/admin/analytics/recent-signups${includeTest ? "?limit=50&include_test=1" : "?limit=50"}`).then(r => setRecent(r.data)).catch(() => setRecent({ error: true }));
  }, [includeTest]);

  return (
    <div className="mt-6 space-y-8" data-testid="admin-analytics">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="text-[11px] text-muted leading-snug max-w-2xl">
          Aggregate metrics from DB + telemetry. No per-user browse view.
          Wave 2 (page_events) is live - product engagement, concierge funnel, pages, device split, and zero-engagement gaps now feed from real route hits.
        </div>
        <label className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted font-bold cursor-pointer" data-testid="analytics-include-test-toggle">
          <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} className="accent-elec"/>
          Include test accounts
        </label>
      </div>

      {/* Tier 1.1 - Signups over time */}
      <Section title="Signups over time" icon={TrendingUp} testid="analytics-signups">
        {!signups ? <Loading/> : signups.error ? <ErrorBox/> : <SignupsPanel data={signups}/>}
      </Section>

      {/* iter96 (PP) — Signups by source. Real attribution: ?src=iima on the
          signup URL is captured server-side and grouped here. `direct` is the
          default for anyone landing without a UTM. Test-filtered by default. */}
      <Section title="Signups by source" icon={Filter} testid="analytics-signups-by-source">
        {!bySource ? <Loading/> : bySource.error ? <ErrorBox/> : (
          bySource.total === 0 ? (
            <div className="moe-card p-6 text-sm text-muted italic" data-testid="signups-by-source-empty">
              No signups yet to attribute.
            </div>
          ) : (
            <div className="moe-card overflow-hidden">
              <table className="w-full text-sm" data-testid="signups-by-source-table">
                <thead className="bg-black/5 text-[10px] uppercase tracking-widest text-muted">
                  <tr>
                    <th className="text-left p-3">Source</th>
                    <th className="text-right p-3">Signups</th>
                    <th className="text-right p-3">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {bySource.rows.map(r => (
                    <tr key={r.source} className="border-t border-ink/10" data-testid={`signups-source-row-${r.source}`}>
                      <td className="p-3 font-semibold">{r.source}</td>
                      <td className="p-3 text-right tabular-nums">{r.count}</td>
                      <td className="p-3 text-right tabular-nums text-muted">
                        {bySource.total > 0 ? Math.round((r.count / bySource.total) * 100) : 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-ink/20 bg-black/[0.02]">
                    <td className="p-3 text-[10px] uppercase tracking-widest text-muted font-bold">Total</td>
                    <td className="p-3 text-right tabular-nums font-bold" data-testid="signups-by-source-total">{bySource.total}</td>
                    <td className="p-3 text-right tabular-nums text-muted">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )
        )}
      </Section>

      {/* iter96 (QQ) — "Who's Arriving" recent signups feed. Real, scannable
          list of the last 50 real students (test-filtered). Not a chart —
          this is the "who just joined" view the founder asked for. */}
      <Section title="Who's arriving (recent signups)" icon={Calendar} testid="analytics-recent-signups">
        {!recent ? <Loading/> : recent.error ? <ErrorBox/> : (
          recent.count === 0 ? (
            <div className="moe-card p-6 text-sm text-muted italic" data-testid="recent-signups-empty">
              No signups yet.
            </div>
          ) : (
            <div className="moe-card overflow-hidden">
              <div className="px-3 pt-3 pb-2 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-muted font-bold">
                  Most recent · {recent.count} shown
                </div>
              </div>
              <table className="w-full text-sm" data-testid="recent-signups-table">
                <thead className="bg-black/5 text-[10px] uppercase tracking-widest text-muted">
                  <tr>
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Home university</th>
                    <th className="text-left p-3">Host university</th>
                    <th className="text-left p-3">City · Country</th>
                    <th className="text-left p-3">Source</th>
                    <th className="text-left p-3">Signed up</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.rows.map(r => (
                    <tr
                      key={r.user_id}
                      className="border-t border-ink/10 hover:bg-black/5 cursor-pointer"
                      onClick={() => setOpenUser({ id: r.user_id })}
                      data-testid={`recent-signup-row-${r.user_id}`}
                    >
                      <td className="p-3 font-semibold" data-testid={`recent-signup-name-${r.user_id}`}>{r.name || "—"}</td>
                      <td className="p-3">{r.home_university || <span className="text-muted italic">Not set</span>}</td>
                      <td className="p-3">{r.host_university || <span className="text-muted italic">Not set</span>}</td>
                      <td className="p-3">
                        {r.host_city ? `${r.host_city} · ${r.country || "—"}` : (r.country || <span className="text-muted italic">Not set</span>)}
                      </td>
                      <td className="p-3">
                        <span
                          className="inline-block px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-elec/10 text-elec"
                          data-testid={`recent-signup-source-${r.user_id}`}
                        >
                          {r.source || "direct"}
                        </span>
                      </td>
                      <td className="p-3 text-muted tabular-nums text-[12px]" data-testid={`recent-signup-when-${r.user_id}`}>
                        {r.signed_up_at ? new Date(r.signed_up_at).toISOString().slice(0, 19).replace("T", " ") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </Section>

      {/* Tier 1.2 - Conversion funnel */}
      <Section title="Conversion funnel" icon={Filter} testid="analytics-funnel">
        {!funnel ? <Loading/> : funnel.error ? <ErrorBox/> : <FunnelPanel data={funnel}/>}
      </Section>

      {/* Tier 1.4 - Moments health */}
      <Section title="Moments health" icon={Home} testid="analytics-moments">
        {!moments ? <Loading/> : moments.error ? <ErrorBox/> : <MomentsPanel data={moments}/>}
      </Section>

      {/* iter83 Wave 2: Tier 1.3 - Product engagement */}
      <Section title="Product engagement (last 30 days)" icon={TrendingUp} testid="analytics-engagement">
        {!engagement ? <Loading/> : engagement.error ? <ErrorBox/> : (
          <div className="moe-card overflow-hidden">
            <table className="w-full text-sm" data-testid="engagement-table">
              <thead className="bg-black/5 text-[10px] uppercase tracking-widest text-muted">
                <tr><th className="text-left p-3">Product</th><th className="text-right p-3">Views</th><th className="text-right p-3">Core actions</th><th className="text-right p-3">Action rate</th></tr>
              </thead>
              <tbody>
                {engagement.products.map(p => (
                  <tr key={p.product} className="border-t border-ink/10" data-testid={`engagement-row-${p.product}`}>
                    <td className="p-3 font-semibold">{p.product}</td>
                    <td className="p-3 text-right tabular-nums">{p.views}</td>
                    <td className="p-3 text-right tabular-nums">{p.actions}</td>
                    <td className="p-3 text-right tabular-nums">{p.action_rate_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* iter83 Wave 2: Tier 1.5 - Concierge funnel */}
      <Section title="Concierge funnel (last 30 days)" icon={Filter} testid="analytics-concierge-funnel">
        {!concFunnel ? <Loading/> : concFunnel.error ? <ErrorBox/> : <FunnelPanel data={concFunnel}/>}
      </Section>

      {/* iter83 Wave 2: Tier 2.6, 2.7, 2.8 - Pages / dropoff / device */}
      <Section title="Pages, drop-off, and device split (last 30 days)" icon={ArrowDownRight} testid="analytics-pages">
        {!pages ? <Loading/> : pages.error ? <ErrorBox/> : (
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <Kpi label="Mobile page views" value={pages.device_split.mobile || 0}/>
              <Kpi label="Desktop page views" value={pages.device_split.desktop || 0}/>
            </div>
            <div className="moe-card p-4">
              <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Top pages</div>
              <ul className="mt-2 text-sm divide-y divide-ink/8" data-testid="pages-top-list">
                {pages.top_pages.slice(0, 15).map(p => (
                  <li key={p.path} className="flex items-center justify-between py-1.5 gap-3">
                    <span className="font-mono text-[12px] truncate">{p.path}</span>
                    <span className="text-[11px] tabular-nums shrink-0"><b>{p.views}</b> views · {p.unique_cases} unique</span>
                  </li>
                ))}
                {pages.top_pages.length === 0 && <li className="text-muted italic text-[12px]">No events yet.</li>}
              </ul>
            </div>
            <div className="moe-card p-4">
              <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Onboarding flow drop-off</div>
              <ul className="mt-2 text-sm space-y-1">
                {pages.onboarding_flow.map((s, i) => (
                  <li key={s.path} className="flex items-center justify-between" data-testid={`onboarding-stage-${i}`}>
                    <span>{i + 1}. {s.label}</span>
                    <span className="tabular-nums"><b>{s.count}</b> <span className="text-[11px] text-muted">· {s.retained_pct}% retained</span></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Section>

      {/* iter83 Wave 2: Tier 3.11 - Zero-engagement countries */}
      <Section title="Zero-engagement countries (Compass, last 30 days)" icon={Clock} testid="analytics-zero-engagement">
        {!zeroEng ? <Loading/> : zeroEng.error ? <ErrorBox/> : (
          <div className="grid gap-3">
            <div className="grid grid-cols-3 gap-3">
              <Kpi label="Total countries" value={zeroEng.total_countries}/>
              <Kpi label="Zero views (30d)" value={zeroEng.zero_view_count} accent={zeroEng.zero_view_count > 0 ? "yolk" : "mint"}/>
              <Kpi label="Engaged countries" value={zeroEng.total_countries - zeroEng.zero_view_count}/>
            </div>
            <div className="moe-card p-4">
              <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Countries with no Compass views</div>
              <div className="mt-2 flex flex-wrap gap-1.5 text-[12px]" data-testid="zero-engagement-list">
                {zeroEng.zero_view_countries.map(c => (
                  <span key={c} className="px-2 py-0.5 rounded-full bg-[color:var(--moe-yolk)]/40 border border-[color:var(--moe-yolk)] font-semibold">{c}</span>
                ))}
                {zeroEng.zero_view_count === 0 && <span className="italic text-muted">All countries have engagement.</span>}
              </div>
            </div>
          </div>
        )}
      </Section>

      {/* Tier 3.9 - Playbook staleness */}
      <Section title="Country playbook staleness" icon={Clock} testid="analytics-playbooks">
        {!playbooks ? <Loading/> : playbooks.error ? <ErrorBox/> : <PlaybooksPanel data={playbooks}/>}
      </Section>

      {/* Tier 3.10 - Festival freshness */}
      <Section title="Festival TBA freshness" icon={PartyPopper} testid="analytics-festivals">
        {!festivals ? <Loading/> : festivals.error ? <ErrorBox/> : <FestivalsPanel data={festivals}/>}
      </Section>

      {/* iter97 (RR) — user detail drawer opened from Who's Arriving rows */}
      {openUser && (
        <UserDetailDrawer
          userId={openUser.id}
          onClose={() => setOpenUser(null)}
          onChanged={() => {
            // refresh recent signups so status chips (if surfaced later) stay current
            const q = includeTest ? "?limit=50&include_test=1" : "?limit=50";
            api.get(`/admin/analytics/recent-signups${q}`).then(r => setRecent(r.data)).catch(() => {});
          }}
        />
      )}
    </div>
  );
}

function Section({ title, icon: Icon, testid, children }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-elec font-bold">
        <Icon size={12} strokeWidth={2.4}/> {title}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

const Loading = () => <div className="moe-shimmer h-24 w-full"/>;
const ErrorBox = () => <div className="moe-card p-4 text-sm text-muted italic">Couldn't load - are you logged in as admin?</div>;

/* ── Signups ───────────────────────────────────────────────────────── */
function SignupsPanel({ data }) {
  // Bar-chart-ish: render as a horizontal bar of daily counts, plus totals.
  const maxCount = Math.max(1, ...data.series.map(d => d.count));
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total students" value={data.total_students}/>
        <Kpi label={`Signups last ${data.window_days} days`} value={data.series.reduce((a, b) => a + b.count, 0)}/>
        <Kpi label="Countries with cases" value={data.by_host_country.length}/>
        <Kpi label="Universities with cases" value={data.by_home_university_top_20.length}/>
      </div>

      <div className="moe-card p-4">
        <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Daily signups (last {data.window_days} days)</div>
        <div className="mt-3 flex items-end gap-[2px] h-32" data-testid="signups-chart">
          {data.series.map(d => (
            <div key={d.date} title={`${d.date}: ${d.count}`} className="flex-1 bg-elec/70 rounded-t"
                 style={{ height: `${Math.max(2, (d.count / maxCount) * 100)}%` }}/>
          ))}
        </div>
        <div className="mt-2 flex justify-between text-[10px] text-muted">
          <span>{data.series[0]?.date}</span>
          <span>{data.series[data.series.length - 1]?.date}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="moe-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted font-bold">By host country</div>
          <ul className="mt-2 text-sm divide-y divide-ink/8">
            {data.by_host_country.map(row => (
              <li key={row.country} className="flex items-center justify-between py-1.5">
                <span className="truncate">{row.country}</span>
                <span className="font-bold tabular-nums">{row.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="moe-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted font-bold">By home university (top 20)</div>
          <ul className="mt-2 text-sm divide-y divide-ink/8">
            {data.by_home_university_top_20.map(row => (
              <li key={row.university} className="flex items-center justify-between py-1.5">
                <span className="truncate">{row.university}</span>
                <span className="font-bold tabular-nums">{row.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ── Funnel ────────────────────────────────────────────────────────── */
function FunnelPanel({ data }) {
  const max = Math.max(1, ...data.stages.map(s => s.count));
  return (
    <div className="moe-card p-4" data-testid="funnel-chart">
      <div className="space-y-2.5">
        {data.stages.map((s, i) => {
          const width = `${(s.count / max) * 100}%`;
          const isDropoff = i > 0 && s.retained_pct < 100;
          return (
            <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
              <div className="flex items-baseline justify-between text-sm">
                <span className="font-semibold text-ink">{i + 1}. {s.label}</span>
                <span className="tabular-nums">
                  <b className="text-ink">{s.count}</b>
                  {i > 0 && (
                    <span className={`ml-2 text-[11px] ${s.retained_pct < 50 ? "text-[color:var(--moe-pink)]" : "text-muted"}`}>
                      {s.retained_pct}% retained
                      {isDropoff && <> · <ArrowDownRight size={10} className="inline"/> {s.dropped} dropped</>}
                    </span>
                  )}
                </span>
              </div>
              <div className="mt-1 h-6 rounded-full bg-ink/8 overflow-hidden">
                <div className="h-full bg-elec" style={{ width }}/>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 text-[11px] text-muted leading-snug">
        "Created first Moment" is intersected with currently-opted-in profiles so the funnel stays monotonic. Moments created by cases that have since opted out don't inflate the last stage.
      </div>
    </div>
  );
}

/* ── Moments health ───────────────────────────────────────────────── */
function MomentsPanel({ data }) {
  const TYPE_LABEL = { arrival: "Arrival", roommate: "Roommate", trip: "Trip", social: "Social" };
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Total Moments" value={data.overall.total}/>
        <Kpi label="Planning" value={data.overall.planning}/>
        <Kpi label="Confirmed" value={data.overall.confirmed}/>
        <Kpi label="Completed" value={data.overall.completed}/>
        <Kpi label="Cancelled" value={data.overall.cancelled}/>
      </div>

      <div className="moe-card overflow-hidden">
        <table className="w-full text-sm" data-testid="moments-by-type-table">
          <thead className="bg-black/5 text-[10px] uppercase tracking-widest text-muted">
            <tr>
              <th className="text-left p-3">Type</th>
              <th className="text-right p-3">Created</th>
              <th className="text-right p-3">Open</th>
              <th className="text-right p-3">Completed</th>
              <th className="text-right p-3">Cancelled</th>
              <th className="text-right p-3">Completion %</th>
            </tr>
          </thead>
          <tbody>
            {data.by_type.map(r => (
              <tr key={r.type} className="border-t border-ink/10" data-testid={`moments-row-${r.type}`}>
                <td className="p-3 font-semibold">{TYPE_LABEL[r.type] || r.type}</td>
                <td className="p-3 text-right tabular-nums">{r.created}</td>
                <td className="p-3 text-right tabular-nums">{r.open}</td>
                <td className="p-3 text-right tabular-nums">{r.completed}</td>
                <td className="p-3 text-right tabular-nums">{r.cancelled}</td>
                <td className="p-3 text-right tabular-nums">{r.completion_rate_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-muted leading-snug">
        Roommate "Completed" reads as "Found a place" in the user-facing UI (same underlying status).
      </div>
    </div>
  );
}

/* ── Playbook staleness ─────────────────────────────────────────────── */
function PlaybooksPanel({ data }) {
  return (
    <div className="grid gap-3">
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Total countries" value={data.total_countries}/>
        <Kpi label={`Stale (>${data.stale_threshold_days}d)`} value={data.stale_count} accent={data.stale_count > 0 ? "pink" : "mint"}/>
        <Kpi label="Fresh" value={data.fresh_count}/>
      </div>
      <div className="moe-card overflow-hidden">
        <table className="w-full text-sm" data-testid="playbooks-staleness-table">
          <thead className="bg-black/5 text-[10px] uppercase tracking-widest text-muted">
            <tr>
              <th className="text-left p-3">Country</th>
              <th className="text-left p-3">Last verified</th>
              <th className="text-right p-3">Days ago</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.countries.map(r => (
              <tr key={r.country} className="border-t border-ink/10" data-testid={`playbook-row-${r.country}`}>
                <td className="p-3 font-semibold">{r.country}</td>
                <td className="p-3 text-muted">{r.last_verified_date || "Never"}</td>
                <td className="p-3 text-right tabular-nums">{r.days_since_verified ?? "-"}</td>
                <td className="p-3">
                  {r.is_stale ? (
                    <span className="moe-chip moe-chip-yolk"><Clock size={11}/> Re-check</span>
                  ) : (
                    <span className="text-[11px] text-muted">Fresh</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Festivals freshness ────────────────────────────────────────────── */
function FestivalsPanel({ data }) {
  return (
    <div className="grid gap-3">
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Total festivals" value={data.total_festivals}/>
        <Kpi label="Covered countries" value={`${data.covered_countries} / ${data.total_target_countries}`}/>
        <Kpi label="TBA (next edition)" value={data.tba_count} accent={data.tba_count > 0 ? "yolk" : "mint"}/>
      </div>
      <div className="moe-card overflow-hidden">
        <table className="w-full text-sm" data-testid="festivals-tba-table">
          <thead className="bg-black/5 text-[10px] uppercase tracking-widest text-muted">
            <tr>
              <th className="text-left p-3">Country</th>
              <th className="text-left p-3">Festival</th>
              <th className="text-left p-3">Current edition shown</th>
              <th className="text-left p-3">Source</th>
            </tr>
          </thead>
          <tbody>
            {data.tba_festivals.map(f => (
              <tr key={f.id} className="border-t border-ink/10" data-testid={`festival-tba-row-${f.id}`}>
                <td className="p-3 font-semibold">{f.country}</td>
                <td className="p-3">{f.name}</td>
                <td className="p-3 text-muted tabular-nums">{f.start_date}</td>
                <td className="p-3"><a href={f.source_url} target="_blank" rel="noopener noreferrer" className="text-elec underline decoration-dotted underline-offset-2">Check source</a></td>
              </tr>
            ))}
            {data.tba_count === 0 && (
              <tr><td colSpan={4} className="p-6 text-center text-muted italic">All festivals have confirmed next-edition dates.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  const cls = accent === "pink" ? "bg-[color:var(--moe-pink)]/10 border-[color:var(--moe-pink)]/30"
            : accent === "yolk" ? "bg-[color:var(--moe-yolk)]/20 border-[color:var(--moe-yolk)]"
            : accent === "mint" ? "bg-[color:var(--moe-mint)] border-elec/30"
            :                     "moe-card";
  return (
    <div className={`p-3 rounded-lg border ${cls}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted font-bold">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
